import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToast } from "../../../_context/ToastContext/ToastContext";
import {
  useCreateUpsellMutation,
  useEditUpsellMutation,
  useGetUpsellDefaultsQuery,
} from "../../../_features/upsellDownsell/api/upsellApiSlice";
import { UPSELL_WIZARD_STEPS } from "../constants";
import {
  calculateCompleteProration,
  getBillingPeriodDates,
} from "../utils/prorationUtils";
import { toBillingSubmitPayload } from "../../../_components/billing/toSubmitShape";
import { fromBillingSubmitPayload } from "../../../_components/billing/fromSubmitShape";
import { htmlToPlainText } from "../../../_utils/htmlToPlainText";

const TOTAL_STEPS = UPSELL_WIZARD_STEPS.length;

// A plan/product sub-document on a saved Subscription can be missing its
// name entirely - some legacy records carry nothing beyond `_id` (confirmed
// live). Falls back to the matching catalog entity's name by `_id`, the
// same fallback `fromAccordionItem` (fromSubmitShape.js) already applies on
// the NEW side of the review comparison.
function resolveItemName(item, nameField, catalog) {
  const ownName = item?.[nameField] ?? item?.name ?? item?.planName ?? item?.productName;
  if (ownName) return ownName;
  const entityId = item?._id?._id ?? item?._id ?? null;
  const catalogEntity = Array.isArray(catalog) ? catalog.find((entity) => entity._id === entityId) : null;
  return catalogEntity?.[nameField] ?? catalogEntity?.name ?? "";
}

// Some subscriptions have no `market` at all - most commonly ones created by
// the self-serve Stripe checkout flow, a serverside bug still live as of
// 2026-08-03 (see simplifyserver/docs/known-issues.md) - confirmed live
// against 67 of 218 real subscriptions. `market` is required to create an
// upsell (`upsellordownsell.controller.js`'s createUpsell 400s without it),
// so a subscription missing it can't have an upsell created against it at
// all otherwise. Every plan/product in the catalog belongs to exactly one
// market, so the subscription's own first plan (or, failing that, first
// product) is a reliable fallback source - same `_id`-match lookup as
// `resolveItemName` above.
export function resolveMissingMarket(subscription, catalogs) {
  const ownMarket = subscription?.marketId || subscription?.market;
  if (ownMarket) return ownMarket;

  const findCatalogMarket = (items, catalog) => {
    for (const item of items || []) {
      const entityId = item?._id?._id ?? item?._id ?? null;
      const catalogEntity = Array.isArray(catalog) ? catalog.find((entity) => entity._id === entityId) : null;
      if (catalogEntity?.market) return catalogEntity.market;
    }
    return null;
  };

  return (
    findCatalogMarket(subscription?.plans, catalogs?.plans) ||
    findCatalogMarket(subscription?.products, catalogs?.products) ||
    null
  );
}

// A saved Subscription document's `discount` field is sometimes an array
// (`[{type, value}]`, one element) rather than the plain `{type, value}`
// object the review/proration comparison expect, and can be present but
// missing `type`/`value` entirely (confirmed live - a real subscription's
// discount was just `[{_id}]`) - previously only a fully-falsy discount got
// the default, so a present-but-incomplete one rendered as "undefined
// discount" in the review step. Backfilled per-field here, same as
// `fromDiscount` (fromSubmitShape.js) already does for the NEW side. Also
// backfills each plan/product's name via `resolveItemName` above - the same
// gap `fromAccordionItem` closes for the NEW side, needed here too since
// `selectedSubscription` is read directly in `UpsellReviewStep`, not through
// that adapter. Also backfills a missing `market` via `resolveMissingMarket`
// above - `onSubmit` reads `selectedSubscription?.marketId ||
// selectedSubscription?.market` directly, so fixing it here is what actually
// makes a previously-market-less subscription submittable, not just
// display-correct. This only normalizes `selectedSubscription` (the nested
// "current state" side of the review comparison) - the NEW side comes from
// `pricing` via `toBillingSubmitPayload`.
export function normalizeSubscriptionForReview(subscription, catalogs) {
  if (!subscription) return subscription;
  const rawDiscount = Array.isArray(subscription.discount) ? subscription.discount[0] : subscription.discount;
  const discount = { type: rawDiscount?.type || "Flat", value: rawDiscount?.value ?? "0" };
  const plans = (subscription.plans || []).map((plan) => ({
    ...plan,
    planName: resolveItemName(plan, "planName", catalogs?.plans),
  }));
  const products = (subscription.products || []).map((product) => ({
    ...product,
    productName: resolveItemName(product, "productName", catalogs?.products),
  }));
  const market = resolveMissingMarket(subscription, catalogs);
  return { ...subscription, discount, plans, products, market };
}

const DEFAULT_VALUES = {
  title: "",
  type: "Upsell",
  description: "",
  company: null,
  subscription: null,
  billingCycle: "",
  expiryIn: 5,
  netTerms: 0,
  upsellBehaviour: "",
  effectiveDate: "",
  effectiveDateDays: "",
  requiresCustomerConfirmation: false,
  onInvoice: false,
  regenerateInvoice: false,
  sendInvoice: false,
  resendProposal: false,
  sendTo: [],
  closedBy: null,
  // Same deliberate call as _features/proposals' useProposalWizard - the old
  // wizard defaulted both of these to "" too (no boilerplate legal copy was
  // ever hardcoded here), so nothing is being dropped by leaving them empty.
  proposalTerms: "",
  invoiceTerms: "",
};

// CompanySelect (_components/form/CompanySelect) displays `value.name` - a
// synthetic field it adds itself when mapping its own search-query results
// (`companyName`/`individualName` -> `name`). A populated Company document
// coming straight from the backend (`customerId` here) never carries that
// field, so passing it through unchanged left the picker showing blank on
// edit even though the value was technically set - confirmed live editing a
// real Tesla Inc upsell. Same normalization already exists independently in
// useProposalWizard.js/useSubscriptionForm.js/useInvoiceForm.js for the
// identical CompanySelect contract; mirrored here.
export function normalizeCompanyForSelect(company) {
  if (!company) return null;
  return { ...company, name: company.isIndividual ? company.individualName : company.companyName };
}

export function mapUpsellToFormValues(data) {
  const company = normalizeCompanyForSelect(
    (data?.customerId && typeof data.customerId === "object" && data.customerId) ||
      (data?.company && typeof data.company === "object" && data.company) ||
      null
  );
  const subscription =
    (data?.subscription && typeof data.subscription === "object" && data.subscription) || null;

  return {
    title: data?.title || "",
    type: data?.type || "Upsell",
    // description is a plain <textarea> today, but records saved while this
    // field was still a rich-text editor carry real HTML (e.g. Quill's own
    // `<p>...</p>` wrapping) - normalize it back to plain text so it doesn't
    // render as literal tags (SCRUM-156).
    description: htmlToPlainText(data?.description) || "",
    company,
    subscription,
    billingCycle: data?.billingCycle || subscription?.billingCycle || "",
    expiryIn: data?.expiryIn ?? 5,
    netTerms: data?.netTerms ?? 0,
    upsellBehaviour: data?.upsellBehaviour || "",
    effectiveDate: data?.effectiveDate || "",
    effectiveDateDays: data?.effectiveDateDays || "",
    requiresCustomerConfirmation: Boolean(data?.requiresCustomerConfirmation),
    onInvoice: Boolean(data?.onInvoice),
    regenerateInvoice: false, // never carried over from a prior save
    sendInvoice: false,
    resendProposal: false,
    sendTo: data?.sendTo || [],
    closedBy: data?.closedBy || null,
    proposalTerms: data?.proposalTerms || "",
    invoiceTerms: data?.invoiceTerms || "",
  };
}

/**
 * Orchestration hook for the upsell/downsell wizard - mirrors
 * _features/proposals/hooks/useProposalWizard.js's shape exactly (one
 * hoisted react-hook-form instance for all scalar fields across all 4
 * steps, shared by the global modal and both routed page hosts;
 * `currentStep` lives here for the same "3+ hosts would otherwise
 * reimplement identical step-nav state" reason documented there).
 *
 * Billing items (plans/products/line items/discount/taxes) are NOT RHF
 * fields - same "dynamic collection with computed totals, not a form
 * scalar" classification every other `_components/billing` consumer uses.
 * The host owns `usePricingState()` and passes it in as `pricing` (same
 * pattern as `useSubscriptionForm`/`useProposalWizard`) - this used to be
 * a deliberate, documented exception (the old-tree `BillingItemsTable` was
 * kept specifically because it could hydrate from an existing
 * subscription's already-priced items, which the new engine's
 * `usePricingState` had no way to do). That gap is closed now:
 * `usePricingState(initial)` always accepted an `initial` argument (it was
 * just never called with one anywhere in the app), and
 * `fromBillingSubmitPayload` (the inverse of `toBillingSubmitPayload`,
 * `_components/billing/fromSubmitShape.js`) produces exactly that shape
 * from a saved Subscription/Upsell record. See docs/bug-sheet.md for the
 * migration and docs/external-dependency-audit.md (BillingItems entry
 * removed).
 *
 * `billingItemsData` is still returned under that name (consumed by
 * `UpsellReviewStep`/`prorationUtils`, both of which read the NESTED
 * `accordionData` shape) - but it's now a derived comparison view built
 * from `pricing` via `toBillingSubmitPayload`, not separate state. Real
 * state lives only in `pricing`.
 */
export function useUpsellWizard({
  mode = "create",
  initialData,
  seedData,
  pricing,
  catalogs,
  onSuccess,
  onClose,
} = {}) {
  const isEdit = mode === "edit";
  const isPreview = mode === "preview";
  const isDuplicate = mode === "duplicate";
  const { showToast } = useToast();
  const currentUser = useSelector((state) => state.auth.user);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const form = useForm({ defaultValues: DEFAULT_VALUES, mode: "onBlur" });
  const { control, watch, setValue, getValues, trigger, handleSubmit, reset } = form;

  const billingCycle = watch("billingCycle");

  // The one function consumers actually call composes plans/products/
  // lineItems/taxes into the nested accordionData shape at submit time;
  // reused here to give the review step / proration calculator a NESTED
  // "new state" view to compare against `selectedSubscription` (also
  // nested) without either of them needing to know about the engine's flat
  // internal shape. `discount` is overridden back to the plain object form
  // `toBillingSubmitPayload` converts to an array - review/proration read
  // `.discount.type`/`.discount.value` directly, matching `pricing.discount`
  // itself (`usePricingState` already holds it as an object).
  const billingItemsData = useMemo(
    () => ({
      ...toBillingSubmitPayload(pricing, { selectedBillingCycle: billingCycle }),
      discount: pricing.discount,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pricing.plans, pricing.products, pricing.lineItems, pricing.discount, pricing.taxes, billingCycle]
  );

  // Replaces the old `totalAmountValue` state (previously pushed up from
  // BillingItemsTable's `setFinalAmount` callback) - `pricing.totals` is
  // already the single source of truth every other consumer's PriceSummary
  // reads, so this is a plain derivation, not a second copy to keep in sync.
  const totalAmountValue = pricing.totals.totalAmount.toFixed(2);

  // Full edit/preview/duplicate record - reset the whole form once it
  // arrives. Split from the pricing-seed effect below (which has its own,
  // later trigger condition) so the scalar fields (title, company, ...)
  // hydrate immediately rather than waiting on catalogs to load.
  const formHydratedForRef = useRef(null);
  useEffect(() => {
    if (initialData && formHydratedForRef.current !== initialData) {
      formHydratedForRef.current = initialData;
      reset({ ...DEFAULT_VALUES, ...mapUpsellToFormValues(initialData) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Platform Settings > Billing > "On upsell" is the starting "Upsell behaviour" of a NEW upsell.
  // Only a starting value: the person can change it, and each upsell keeps its own behaviour (that is
  // what execution reads). Never overrides one already set (a duplicate of an upsell that has one),
  // never applies to an edit/preview or a downsell ("On upsell" is about upsells). Declared after the
  // initialData reset effect so a duplicate's own value is in place before this looks at it.
  const { data: upsellDefaults } = useGetUpsellDefaultsQuery(undefined, { skip: isEdit || isPreview });
  const defaultBehaviour = upsellDefaults?.data?.upsellBehaviour;
  const wizardType = watch("type");
  useEffect(() => {
    // getValues, not the render-time wizardType: on a duplicate the reset above has just changed the type
    if (isEdit || isPreview || !defaultBehaviour || getValues("type") === "Downsell") return;
    if (!getValues("upsellBehaviour")) setValue("upsellBehaviour", defaultBehaviour);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, isPreview, defaultBehaviour, wizardType, initialData]);

  // Sets `selectedSubscription` for the review comparison - split from the
  // form-reset effect above (which must fire immediately, not wait on
  // catalogs) because `normalizeSubscriptionForReview`'s name backfill needs
  // `catalogs.plans`/`.products` to have actually loaded, the same "async
  // data arrives after mount" race `pricingHydratedForRef` below already
  // guards against for the identical `catalogs` dependency.
  const selectedSubscriptionHydratedForRef = useRef(null);
  useEffect(() => {
    if (!initialData || typeof initialData.subscription !== "object" || !initialData.subscription) return;
    const catalogsLoaded = catalogs?.plans !== undefined && catalogs?.products !== undefined;
    if (!catalogsLoaded || selectedSubscriptionHydratedForRef.current === initialData) return;
    selectedSubscriptionHydratedForRef.current = initialData;
    setSelectedSubscription(normalizeSubscriptionForReview(initialData.subscription, catalogs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, catalogs?.plans, catalogs?.products]);

  // Hydrates `pricing` straight from the saved record's nested plans/
  // products/lineItems/discount/taxes via `fromBillingSubmitPayload` (same
  // "async data arrives after mount, re-seed via effect" shape as the RHF
  // reset above - `usePricingState()`'s `initial` argument only ever seeds
  // a `useState` initializer, which runs once, before `initialData` has
  // actually loaded). Deliberately waits for `catalogs.plans`/`.products`
  // to have actually loaded (not just defaulted to `[]` by the host while
  // the query is still in flight) before running, and runs at most once per
  // `initialData` - `fromBillingSubmitPayload`'s pricingCycle catalog-
  // lookup fallback (see fromSubmitShape.js's docstring: UpsellDownsell's
  // plan/product sub-schema doesn't persist its own pricingCycle at all)
  // needs real catalog data to search, and since this effect only runs
  // once, seeding early with an empty catalog would silently and
  // permanently skip that fallback for the rest of this page's lifetime.
  const pricingHydratedForRef = useRef(null);
  useEffect(() => {
    if (!initialData || pricingHydratedForRef.current === initialData) return;
    const catalogsLoaded = catalogs?.plans !== undefined && catalogs?.products !== undefined;
    if (!catalogsLoaded) return;
    pricingHydratedForRef.current = initialData;

    const seeded = fromBillingSubmitPayload(initialData, {
      selectedBillingCycle: initialData.billingCycle || initialData.subscription?.billingCycle,
      planCatalog: catalogs.plans,
      productCatalog: catalogs.products,
    });
    pricing.setPlans(seeded.plans);
    pricing.setProducts(seeded.products);
    pricing.setLineItems(seeded.lineItems);
    pricing.setDiscount(seeded.discount);
    pricing.setTaxes(seeded.taxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, catalogs?.plans, catalogs?.products]);

  // Lightweight seed (from CompanyUpsells' "+ Add upsell" on a company, or
  // SubscriptionUpsells' on a subscription) - just pre-fills the company/
  // subscription pickers, not a full record, so it only ever applies on a
  // fresh create (never alongside `initialData`). The seeded subscription
  // is only an id at this point (`seedSubscriptionId`, exposed below) -
  // UpsellPackageDetailsStep resolves it against its own fetched
  // subscriptions-for-company list and calls selectSubscription once a
  // match shows up, same as picking it from the dropdown manually.
  useEffect(() => {
    if (!initialData && seedData) {
      if (seedData.customerId) setValue("company", normalizeCompanyForSelect(seedData.customerId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedData, initialData]);

  const seedSubscriptionId = !initialData
    ? seedData?.subscription || seedData?.subscriptionId
    : null;
  // Only lock the company picker when this wizard was opened from inside a
  // company/subscription's own page (a fixed context) - mirrors the old
  // wizard's `disabled={customerData}` on CompanySelect, which only ever
  // received that prop from CompanyUpsells/SubscriptionUpsells, never from
  // the routed Create/Edit/Preview hosts.
  const lockCompany = Boolean(!initialData && seedData?.customerId);

  const [createUpsell, { isLoading: isCreating }] = useCreateUpsellMutation();
  const [editUpsell, { isLoading: isUpdating }] = useEditUpsellMutation();

  // Validates the CURRENT step's own fields before advancing - the old
  // wizard had no such gate at all (Next always just incremented
  // currentStep). Lives here so every host shares identical gating.
  const goToNextStep = async () => {
    const step = UPSELL_WIZARD_STEPS.find((s) => s.id === currentStep);
    const isValid = await trigger(step?.fields || []);
    if (isValid) setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const goToPreviousStep = () => setCurrentStep((step) => Math.max(step - 1, 1));

  // Step-tab click-to-navigate (UpsellWizardBody's own step tabs, ported
  // from useProposalWizard's identical goToStep) - only ever jumps
  // BACKWARD to an already-visited step, never forward past whatever
  // goToNextStep's validation would otherwise gate.
  const goToStep = (stepId) => {
    if (stepId >= 1 && stepId <= currentStep) setCurrentStep(stepId);
  };

  // ContactsPickerModal reports the FULL selected set on confirm, so this
  // replaces wholesale rather than adding one at a time. `sendTo` ("Send
  // upsell to", step 3) used to sit alongside a separate step-1 "deal
  // contacts" field (`contacts`) - removed at the user's request as a
  // duplicate concept the wizard didn't need two of. `sendTo` is now the
  // one contact list this wizard collects.
  const setSendTo = (contacts) => setValue("sendTo", contacts || []);
  const removeSendToContact = (contactId) => {
    const current = watch("sendTo") || [];
    setValue("sendTo", current.filter((contact) => contact._id !== contactId));
  };

  // Picking a subscription re-seeds `pricing` from it UNLESS the user
  // already has real edits in place for that same subscription - same
  // "don't clobber in-progress edits" guard the old UpsellConfigure had.
  const selectSubscription = (rawSubscription) => {
    if (!rawSubscription) return;
    const subscription = normalizeSubscriptionForReview(rawSubscription, catalogs);
    const hasExistingData =
      pricing.plans?.length > 0 || pricing.products?.length > 0 || pricing.lineItems?.length > 0;
    const isDifferentSubscription = subscription._id !== selectedSubscription?._id;

    setValue("subscription", subscription);
    setValue("billingCycle", subscription.billingCycle);
    setSelectedSubscription(subscription);

    if (!hasExistingData || isDifferentSubscription) {
      const seeded = fromBillingSubmitPayload(subscription, {
        selectedBillingCycle: subscription.billingCycle,
        planCatalog: catalogs?.plans,
        productCatalog: catalogs?.products,
      });
      pricing.setPlans(seeded.plans);
      pricing.setProducts(seeded.products);
      pricing.setLineItems(seeded.lineItems);
      pricing.setDiscount(seeded.discount);
      pricing.setTaxes(seeded.taxes);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      let prorationData = null;
      if (selectedSubscription) {
        try {
          const { periodStart, periodEnd, changeDate } = getBillingPeriodDates(selectedSubscription);
          prorationData = calculateCompleteProration({
            periodStart,
            periodEnd,
            changeDate,
            oldSubscription: selectedSubscription,
            newBillingItems: billingItemsData,
          });
        } catch (error) {
          // Proration is a supplementary breakdown for the review step, not
          // a submission blocker - a malformed subscription anchor date
          // shouldn't stop the upsell from saving.
          prorationData = null;
        }
      }

      // The single shared adapter every other `_components/billing`
      // consumer submits through (Subscriptions/Proposals/Invoices) -
      // converts `pricing`'s flat plans/products/lineItems/discount/taxes
      // into the nested `accordionData` shape the UpsellDownsell schema
      // expects (identical nesting to Subscription's, confirmed by reading
      // both schemas directly). No manual per-field mapping or "Select a
      // plan"/"Select a product" placeholder filtering needed here anymore
      // - the new engine's PlanProductPicker never creates a placeholder
      // row with that sentinel name; an unconfigured row just has a null
      // `entityId`, same edge case every other consumer already accepts.
      const billing = toBillingSubmitPayload(pricing, { selectedBillingCycle: values.billingCycle });
      // ContactsPickerModal reports full contact objects on confirm; the
      // server only wants ids for `sendTo` (it re-populates the full
      // details on read).
      const sendToIds = (values.sendTo || []).map((contact) => contact._id);

      const payload = {
        ...(isEdit && initialData?._id && { _id: initialData._id }),
        customer: values.company?._id,
        // The backend's createUpsell controller needs the FULL company
        // object, not just its id - it reads company.address.addr1,
        // company.isIndividual, company.companyName etc. directly (several
        // unguarded, e.g. `company.address.addr1 + ...` with no `?.`) to
        // build the upsell PDF. Sending just an id here 500'd on submit
        // (confirmed live while verifying this migration) - matches how
        // the old wizard's upsellFormData.company was always the full
        // CompanySelect result, never trimmed to an id.
        company: values.company,
        market: selectedSubscription?.marketId || selectedSubscription?.market,
        subscription: selectedSubscription?._id || selectedSubscription?.subscriptionId,
        billingCycle: values.billingCycle,
        status: isEdit ? initialData?.status : "Open",
        stage: isEdit ? initialData?.stage : "Qualified",
        type: values.type || "Upsell",
        // The change against what the customer pays now, not the whole new total - the Net
        // expansion / Upsell MRR cards sum this, so sending the total counted the existing plan again.
        mrrImpact: (Number(totalAmountValue) - Number(selectedSubscription?.orderAmount || 0)).toFixed(2),
        closedBy: values.closedBy,
        orderAmount: totalAmountValue,
        sendTo: sendToIds,
        requiresCustomerConfirmation: values.requiresCustomerConfirmation,
        description: values.description,
        // The UpsellDownsell schema declares both as enums with real
        // defaults ("immediate_payment"/"prorated_invoice") - but a
        // default only applies when the field is absent, not when it's
        // explicitly set to "". Sending "" (this wizard's own unset
        // default, unselected in step 3) hit that exact gap and 500'd on
        // submit as a Mongoose enum validation error (confirmed live while
        // verifying this migration - same payload shape the old wizard
        // sent, so this was already broken for any create that didn't
        // explicitly touch these two fields). Omitting the key when unset
        // lets Mongoose's own default take over, matching what an
        // untouched field is supposed to do.
        effectiveDate: values.effectiveDate || undefined,
        effectiveDateDays: values.effectiveDateDays,
        upsellBehaviour: values.upsellBehaviour || undefined,
        expiryIn: values.expiryIn,
        netTerms: values.netTerms,
        onInvoice: values.onInvoice,
        regenerateInvoice: values.regenerateInvoice,
        sendInvoice: values.sendInvoice,
        resendProposal: values.resendProposal,
        proposalTerms: values.proposalTerms,
        invoiceTerms: values.invoiceTerms,
        ...billing,
        oneTimeFees: [],
        title: values.title,
        totalPriceProposal: {
          currency:
            selectedSubscription?.marketId?.currency?.[0] ||
            selectedSubscription?.market?.currency?.[0] ||
            null,
          totalPrice: totalAmountValue,
          razorpayCode: "",
        },
        prorationData,
        isDuplicateUpsell: isDuplicate,
      };

      const response = isEdit
        ? await editUpsell(payload).unwrap()
        : await createUpsell({ ...payload, initiatedBy: currentUser?._id }).unwrap();

      showToast(`${values.type || "Upsell"} ${isEdit ? "updated" : "created"} successfully`, "success");
      onSuccess?.(response);
      onClose?.();
    } catch (error) {
      showToast(error?.data?.message || "Something went wrong. Please try again.", "error");
    }
  });

  return {
    control,
    watch,
    setValue,
    trigger,
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    onSubmit,
    isSubmitting: isCreating || isUpdating,
    isEdit,
    isPreview,
    setSendTo,
    removeSendToContact,
    pricing,
    billingItemsData,
    totalAmountValue,
    selectedSubscription,
    selectSubscription,
    seedSubscriptionId,
    lockCompany,
  };
}
